import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData, faqData } from '@/mocks/articles';

const websiteUrl = 'https://leyusports.com';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rgts7nahb7382ovmbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '乐鱼体育资讯中心',
    url: websiteUrl,
    description: aboutData.intro,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutData.contact.phone,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contact.address,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: websiteUrl },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 乐鱼体育资讯中心 | 品牌介绍与联系方式"
        description="了解乐鱼体育资讯中心——中国领先的体育资讯平台。品牌介绍、发展历程、行业口碑及联系方式，欢迎商务合作与广告投放咨询。"
        keywords="乐鱼体育资讯中心,关于我们,体育媒体,联系我们,品牌介绍"
        canonicalUrl={`${websiteUrl}/about`}
        schemaJson={{ '@context': 'https://schema.org', '@graph': [orgSchema, faqSchema, breadcrumbSchema] }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <section className="relative w-full h-[350px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20media%20office%20workspace%20with%20sports%20broadcast%20screens%2C%20professional%20newsroom%20environment%2C%20warm%20lighting%2C%20journalists%20working%2C%20clean%20minimalist%20design%2C%20red%20accent%20details%2C%20Chinese%20sports%20media%20company&width=1600&height=900&seq=about-hero&orientation=landscape"
            alt="关于乐鱼体育资讯中心"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">关于我们</h1>
            <p className="text-background-200 max-w-lg">了解乐鱼体育资讯中心的故事与愿景</p>
          </div>
        </section>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">品牌介绍</h2>
            <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto">{aboutData.intro}</p>
          </div>

          <div className="bg-background-100 rounded-2xl p-8 md:p-10 mb-14 text-center">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
              <i className="ri-flag-line text-accent-600 text-xl"></i>
            </div>
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">我们的使命</h3>
            <p className="text-foreground-600 leading-relaxed max-w-xl mx-auto">{aboutData.mission}</p>
          </div>

          <div className="mb-14">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">发展历程</h3>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="flex flex-col gap-6">
                {aboutData.history.map((item, index) => (
                  <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0 md:px-8`}>
                      <div className={`bg-background-50 rounded-xl p-5 border border-background-200/60 ${index % 2 === 0 ? 'md:ml-auto md:mr-0' : 'md:mr-auto md:ml-0'} md:max-w-[380px]`}>
                        <span className="text-sm font-bold text-primary-500">{item.year}</span>
                        <p className="text-sm text-foreground-600 mt-1">{item.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-2.5 md:left-1/2 md:-translate-x-1/2 w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-background-50 mt-1.5 z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16">
          <div className="max-w-[960px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">行业口碑</h2>
              <p className="text-foreground-500">业界对我们的认可与评价</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {aboutData.reputation.map((item) => (
                <div key={item.title} className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                    <i className="ri-star-line text-accent-600"></i>
                  </div>
                  <h4 className="font-heading font-bold text-foreground-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[960px] mx-auto px-4 md:px-6 py-16">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">联系我们</h2>
            <p className="text-foreground-500">欢迎商务合作、广告投放及媒体对接</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-foreground-900 text-sm">公司地址</h4>
                  <p className="text-sm text-foreground-500 mt-1">{aboutData.contact.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-primary-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-foreground-900 text-sm">联系电话</h4>
                  <p className="text-sm text-foreground-500 mt-1">{aboutData.contact.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-foreground-900 text-sm">电子邮箱</h4>
                  <p className="text-sm text-foreground-500 mt-1">{aboutData.contact.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-primary-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-foreground-900 text-sm">工作时间</h4>
                  <p className="text-sm text-foreground-500 mt-1">{aboutData.contact.businessHours}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl border border-background-200/60 p-6">
              <h3 className="font-heading font-bold text-foreground-900 mb-5">给我们留言</h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="true">
                <div>
                  <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="about-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="about-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="about-message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-700' : 'text-red-600'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">常见问题</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-50/80 transition-colors">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:hidden">
                      <i className="ri-add-line"></i>
                    </div>
                    <div className="w-6 h-6 items-center justify-center flex-shrink-0 text-foreground-400 hidden group-open:flex">
                      <i className="ri-subtract-line"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}